import React, { useState } from "react";
import { Button, TextField, Paper, Box, Typography, Divider, IconButton } from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import GroupIcon from "@mui/icons-material/Group";
import LogoutIcon from "@mui/icons-material/Logout";

interface LandingPageProps {
  onPlayVsBots: () => void;
  onCreateRoom: () => void;
  onJoinRoom: (code: string) => void;
  onLogout: () => void;
  userName?: string;
}

export const LandingPage: React.FC<LandingPageProps> = ({
  onPlayVsBots,
  onCreateRoom,
  onJoinRoom,
  onLogout,
  userName,
}) => {
  const [roomCode, setRoomCode] = useState("");

  const handleJoinRoom = () => {
    if (roomCode.trim()) {
      onJoinRoom(roomCode.trim().toUpperCase());
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-emerald-900 to-emerald-950 p-4">
      {/* User info and logout */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        {userName && (
          <span className="text-emerald-300 text-sm">{userName}</span>
        )}
        <IconButton
          onClick={onLogout}
          size="small"
          sx={{ color: "#6ee7b7", "&:hover": { color: "white" } }}
          title="Logout"
        >
          <LogoutIcon />
        </IconButton>
      </div>

      <div className="text-center mb-8">
        <h1 className="text-5xl font-bold text-white mb-2">Königsrufen</h1>
        <p className="text-emerald-300 text-lg">Austrian Tarock Card Game</p>
      </div>

      <Paper elevation={6} className="p-8 max-w-md w-full rounded-xl">
        <div className="flex flex-col gap-4">
          {/* Play vs Bots - Primary Action */}
          <Button
            variant="contained"
            size="large"
            onClick={onPlayVsBots}
            startIcon={<SmartToyIcon />}
            sx={{
              py: 2,
              fontSize: "1.1rem",
              backgroundColor: "#059669",
              "&:hover": { backgroundColor: "#047857" },
            }}
          >
            Play vs Bots
          </Button>

          {/* Create Online Room */}
          <Button
            variant="outlined"
            size="large"
            onClick={onCreateRoom}
            startIcon={<GroupIcon />}
            sx={{
              py: 2,
              fontSize: "1.1rem",
              borderColor: "#059669",
              color: "#059669",
              "&:hover": {
                borderColor: "#047857",
                backgroundColor: "rgba(5, 150, 105, 0.1)",
              },
            }}
          >
            Play with Friends
          </Button>

          <Divider sx={{ my: 2 }}>
            <Typography color="textSecondary" variant="body2">
              or join a friend's game
            </Typography>
          </Divider>

          {/* Join Room */}
          <Box className="flex gap-2">
            <TextField
              placeholder="Room Code"
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && handleJoinRoom()}
              inputProps={{
                maxLength: 6,
                style: { textTransform: "uppercase", letterSpacing: "0.2em" },
              }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleJoinRoom}
              disabled={!roomCode.trim()}
              sx={{
                backgroundColor: "#6b7280",
                "&:hover": { backgroundColor: "#4b5563" },
              }}
            >
              Join
            </Button>
          </Box>
        </div>
      </Paper>

      <p className="text-emerald-400 text-sm mt-8 opacity-70">
        A four-player trick-taking game with Tarot cards
      </p>
    </div>
  );
};
